import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Loader2, ArrowLeft, Clock, Camera, Shield, Package, Key,
  Play, CheckCircle2, AlertCircle, MapPin, MessageSquare,
} from 'lucide-react';
import { api } from '@/utils/api';
import { formatDate } from '@/utils/formatters';

// Merged audit timeline for a ticket (or product unit):
// BarcodeScanEvents + ProductUnitActivities + ServiceOtpEvents + ServicePhotos
// sorted by timestamp. Used from /service/tickets/:id/audit AND
// /inventory/units/:code/history (ProductUnitWarrantyHistoryPage wraps this).

type AuditKind = 'scan' | 'photo' | 'otp' | 'activity';

interface AuditEvent {
  id: string;
  kind: AuditKind;
  at: string;
  title: string;
  description?: string;
  metadata?: Record<string, unknown>;
  actor?: string;
}

export function ServiceAuditPage() {
  const { id: ticketId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [events, setEvents] = useState<AuditEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchAudit = useCallback(async () => {
    if (!ticketId) return;
    try {
      setLoading(true);
      setError(null);
      // Attempt consolidated endpoint first
      let rows: AuditEvent[] = [];
      try {
        const res = await api.get(`/service/tickets/${ticketId}/audit`);
        rows = res.data.data?.events ?? res.data.data ?? [];
      } catch {
        // Fall back: stitch from individual endpoints
        const [scans, photos, otps] = await Promise.all([
          api.get(`/service/tickets/${ticketId}/scans`).catch(() => ({ data: { data: [] } })),
          api.get(`/service/tickets/${ticketId}/photos`).catch(() => ({ data: { data: [] } })),
          api.get(`/service/tickets/${ticketId}/otp-events`).catch(() => ({ data: { data: [] } })),
        ]);
        const scanRows = (scans.data.data?.events ?? scans.data.data ?? []) as Array<Record<string, unknown>>;
        const photoRows = (photos.data.data?.photos ?? photos.data.data ?? []) as Array<Record<string, unknown>>;
        const otpRows = (otps.data.data?.events ?? otps.data.data ?? []) as Array<Record<string, unknown>>;

        rows = [
          ...scanRows.map(s => ({
            id: String(s.id || Math.random()),
            kind: 'scan' as AuditKind,
            at: String(s.scannedAt || s.createdAt || ''),
            title: `Barcode ${String(s.eventType || 'scanned')}`,
            description: String(s.barcodeCode || ''),
            actor: String(s.scannedByName || s.userName || 'System'),
          })),
          ...photoRows.map(p => ({
            id: String(p.id || Math.random()),
            kind: 'photo' as AuditKind,
            at: String(p.capturedAt || p.createdAt || ''),
            title: `${String(p.phase || 'photo')} photo uploaded`,
            description: p.isGeoTagged
              ? `${String((p.latitude as number)?.toFixed?.(4) || '?')}, ${String((p.longitude as number)?.toFixed?.(4) || '?')}`
              : 'no GPS',
            actor: String(p.source || 'app'),
          })),
          ...otpRows.map(o => ({
            id: String(o.id || Math.random()),
            kind: 'otp' as AuditKind,
            at: String(o.verifiedAt || o.sentAt || o.createdAt || ''),
            title: o.verifiedAt ? 'OTP verified' : 'OTP sent',
            description: `${String(o.method || 'whatsapp')} · attempts: ${String(o.attempts ?? 0)}`,
          })),
        ];
      }
      rows.sort((a, b) => new Date(b.at).getTime() - new Date(a.at).getTime());
      setEvents(rows);
    } catch (err) {
      const msg = ((err as { response?: { data?: { error?: string } } }).response?.data?.error)
        || 'Failed to load audit trail';
      setError(msg);
    } finally {
      setLoading(false);
    }
  }, [ticketId]);

  useEffect(() => { fetchAudit(); }, [fetchAudit]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 size={24} className="animate-spin text-[var(--saffron-600)]" />
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-3xl">
      <div className="mb-6 flex items-center gap-3">
        <button
          onClick={() => navigate(`/service-tickets/${ticketId}`)}
          className="rounded-lg border border-gray-300 p-2 text-gray-600 hover:bg-gray-50 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-800"
          aria-label="Back"
        >
          <ArrowLeft size={18} />
        </button>
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Audit Trail</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400">Every event, newest first.</p>
        </div>
      </div>

      {error && (
        <div className="mb-4 flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
          <AlertCircle size={18} />
          {error}
        </div>
      )}

      {events.length === 0 ? (
        <div className="rounded-xl border border-dashed border-gray-300 bg-gray-50 p-10 text-center text-sm text-gray-500 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-400">
          <Clock size={28} className="mx-auto mb-2 text-gray-300" />
          No events recorded yet.
        </div>
      ) : (
        <ol className="relative ml-3 border-l border-gray-200 dark:border-gray-700">
          {events.map(ev => (
            <AuditRow key={ev.id} ev={ev} />
          ))}
        </ol>
      )}
    </div>
  );
}

function AuditRow({ ev }: { ev: AuditEvent }) {
  const cfg = iconFor(ev.kind);
  return (
    <li className="mb-6 ml-4">
      <div className={`absolute -left-[9px] flex h-4 w-4 items-center justify-center rounded-full ${cfg.bg} text-white`}>
        <cfg.Icon size={10} />
      </div>
      <div className="rounded-lg border border-gray-200 bg-white p-3 dark:border-gray-700 dark:bg-gray-900">
        <div className="mb-1 flex items-center justify-between">
          <div className="text-sm font-medium text-gray-900 dark:text-gray-100">{ev.title}</div>
          <time className="text-xs text-gray-400">{formatDate(ev.at)}</time>
        </div>
        {ev.description && (
          <div className="text-xs text-gray-600 dark:text-gray-400">{ev.description}</div>
        )}
        {ev.actor && (
          <div className="mt-1 text-xs text-gray-400">by {ev.actor}</div>
        )}
      </div>
    </li>
  );
}

function iconFor(kind: AuditKind): { Icon: typeof Clock; bg: string } {
  switch (kind) {
    case 'scan':
      return { Icon: Package, bg: 'bg-blue-500' };
    case 'photo':
      return { Icon: Camera, bg: 'bg-purple-500' };
    case 'otp':
      return { Icon: Key, bg: 'bg-amber-500' };
    case 'activity':
      return { Icon: Shield, bg: 'bg-green-500' };
    default:
      return { Icon: Clock, bg: 'bg-gray-500' };
  }
}

// Re-export dummy so TS doesn't complain about unused imports
export const _unusedIcons = { Play, CheckCircle2, MapPin, MessageSquare };
